import React, { useState, useEffect, useCallback } from 'react';
import {
  getReminderSettings, updateReminderSettings, getReminderClients,
  getReminderClientOverride, updateReminderClientOverride,
  getReminderLog, getReminderLogStats, runRemindersNow
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Checkbox
} from '@/components/ui/checkbox';
import {
  Bell, Settings2, Users, FileText, Search, Plus, Trash2, Save, Loader2,
  Mail, MessageCircle, Phone, Smartphone, Zap, AlertTriangle, CheckCircle2,
  XCircle, Clock, Send, Edit, ChevronRight, Info, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const CHANNEL_CONFIG = [
  { key: 'in_app', label: 'In-App', icon: Bell, mock: false },
  { key: 'email', label: 'Email', icon: Mail, mock: true },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, mock: true },
  { key: 'sms', label: 'SMS', icon: Phone, mock: true }
];

const DEFAULT_INTERVAL_OPTIONS = [
  { days: -7, label: '7 days before due' },
  { days: -3, label: '3 days before due' },
  { days: 0, label: 'On due date' },
  { days: 3, label: '3 days overdue' },
  { days: 7, label: '7 days overdue' },
  { days: 14, label: '14 days overdue' },
  { days: 30, label: '30 days overdue' }
];

export default function PaymentRemindersPage() {
  const [activeTab, setActiveTab] = useState('settings');
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Client overrides
  const [clientsData, setClientsData] = useState({ items: [], total: 0 });
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [clientOverride, setClientOverride] = useState(null);
  const [clientSaving, setClientSaving] = useState(false);

  // Reminder log
  const [logs, setLogs] = useState({ items: [], total: 0 });
  const [logStats, setLogStats] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilters, setLogFilters] = useState({ channel: '', status: '' });

  // Fetch global settings
  const fetchSettings = useCallback(async () => {
    try {
      setSettingsLoading(true);
      const res = await getReminderSettings();
      setSettings(res.data);
    } catch (err) {
      toast.error('Failed to load reminder settings');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    try {
      setClientsLoading(true);
      const params = {};
      if (clientSearch) params.search = clientSearch;
      const res = await getReminderClients(params);
      setClientsData(res.data);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setClientsLoading(false);
    }
  }, [clientSearch]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const params = { ...logFilters, limit: 100 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const [logsRes, statsRes] = await Promise.all([
        getReminderLog(params),
        getReminderLogStats()
      ]);
      setLogs(logsRes.data);
      setLogStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load reminder logs');
    } finally {
      setLogsLoading(false);
    }
  }, [logFilters]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { if (activeTab === 'clients') fetchClients(); }, [activeTab, fetchClients]);
  useEffect(() => { if (activeTab === 'log') fetchLogs(); }, [activeTab, fetchLogs]);

  // Save global settings
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateReminderSettings(settings);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Toggle interval
  const toggleInterval = (idx) => {
    const updated = [...settings.intervals];
    updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
    setSettings(s => ({ ...s, intervals: updated }));
  };

  // Toggle channel on interval
  const toggleIntervalChannel = (intervalIdx, channel) => {
    const updated = [...settings.intervals];
    const channels = [...(updated[intervalIdx].channels || [])];
    if (channels.includes(channel)) {
      updated[intervalIdx] = { ...updated[intervalIdx], channels: channels.filter(c => c !== channel) };
    } else {
      updated[intervalIdx] = { ...updated[intervalIdx], channels: [...channels, channel] };
    }
    setSettings(s => ({ ...s, intervals: updated }));
  };

  // Add interval
  const addInterval = (days, label) => {
    const existing = settings.intervals?.find(i => i.days_relative === days);
    if (existing) { toast.info('This interval already exists'); return; }
    setSettings(s => ({
      ...s,
      intervals: [...(s.intervals || []), { days_relative: days, label, channels: ['in_app'], enabled: true }]
        .sort((a, b) => a.days_relative - b.days_relative)
    }));
  };

  // Remove interval
  const removeInterval = (idx) => {
    setSettings(s => ({ ...s, intervals: s.intervals.filter((_, i) => i !== idx) }));
  };

  // Toggle global channel
  const toggleGlobalChannel = (channel) => {
    setSettings(s => ({ ...s, channels: { ...s.channels, [channel]: !s.channels?.[channel] } }));
  };

  // Edit client override
  const handleEditClient = async (client) => {
    setEditingClient(client);
    try {
      const res = await getReminderClientOverride(client.id);
      setClientOverride(res.data.override || {
        override_enabled: false,
        intervals: settings?.intervals || [],
        channels: settings?.channels || {},
        notes: ''
      });
    } catch {
      setClientOverride({ override_enabled: false, intervals: [], channels: {}, notes: '' });
    }
  };

  // Save client override
  const handleSaveClientOverride = async () => {
    if (!editingClient) return;
    setClientSaving(true);
    try {
      await updateReminderClientOverride(editingClient.id, clientOverride);
      toast.success('Client override saved');
      setEditingClient(null);
      fetchClients();
    } catch (err) {
      toast.error('Failed to save override');
    } finally {
      setClientSaving(false);
    }
  };

  // Run now
  const handleRunNow = async () => {
    try {
      const res = await runRemindersNow();
      toast.success(`Reminders sent: ${res.data.sent}, Failed: ${res.data.failed}`);
      if (activeTab === 'log') fetchLogs();
    } catch (err) {
      toast.error('Failed to run reminders');
    }
  };

  return (
    <div data-testid="payment-reminders-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Payment Reminders</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure automated reminders for overdue and upcoming invoices</p>
        </div>
        <Button data-testid="payment-reminders-run-now-button" variant="outline" onClick={handleRunNow}>
          <Zap className="h-4 w-4 mr-2" /> Run Now
        </Button>
      </div>

      {/* Tabs */}
      <Tabs data-testid="payment-reminders-tabs" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="settings"><Settings2 className="h-4 w-4 mr-2" /> Global Settings</TabsTrigger>
          <TabsTrigger value="clients"><Users className="h-4 w-4 mr-2" /> Client Overrides</TabsTrigger>
          <TabsTrigger value="log"><FileText className="h-4 w-4 mr-2" /> Reminder Log</TabsTrigger>
        </TabsList>

        {/* ===== GLOBAL SETTINGS TAB ===== */}
        <TabsContent value="settings" className="mt-6">
          {settingsLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : settings ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left: Schedule Builder */}
              <div className="lg:col-span-7 space-y-6">
                {/* Enable/Disable */}
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Payment Reminders</p>
                        <p className="text-sm text-muted-foreground">Enable or disable all automated reminders</p>
                      </div>
                      <Switch
                        data-testid="payment-reminders-global-enable-switch"
                        checked={settings.enabled}
                        onCheckedChange={(v) => setSettings(s => ({ ...s, enabled: v }))}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Reminder Intervals */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Reminder Schedule</CardTitle>
                    <CardDescription>Intervals are relative to the invoice due date</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {settings.intervals?.map((interval, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
                        <Checkbox
                          checked={interval.enabled}
                          onCheckedChange={() => toggleInterval(idx)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-sm font-medium', !interval.enabled && 'text-muted-foreground line-through')}>
                            {interval.label || `${interval.days_relative > 0 ? '+' : ''}${interval.days_relative} days`}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {CHANNEL_CONFIG.map(ch => (
                              <Badge
                                key={ch.key}
                                variant={interval.channels?.includes(ch.key) ? 'default' : 'outline'}
                                className={cn(
                                  'text-xs cursor-pointer select-none transition-colors',
                                  interval.channels?.includes(ch.key) ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20' : 'opacity-50 hover:opacity-75'
                                )}
                                onClick={() => toggleIntervalChannel(idx, ch.key)}
                              >
                                <ch.icon className="h-3 w-3 mr-1" />
                                {ch.label}
                                {ch.mock && interval.channels?.includes(ch.key) && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild><span className="ml-1 text-[10px] opacity-70">Mock</span></TooltipTrigger>
                                      <TooltipContent>Delivery is simulated in this environment</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeInterval(idx)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}

                    {/* Add interval */}
                    <div className="pt-2">
                      <Select onValueChange={(v) => {
                        const opt = DEFAULT_INTERVAL_OPTIONS.find(o => o.days === parseInt(v));
                        if (opt) addInterval(opt.days, opt.label);
                      }}>
                        <SelectTrigger data-testid="payment-reminders-add-interval-button" className="w-full">
                          <SelectValue placeholder="+ Add reminder interval" />
                        </SelectTrigger>
                        <SelectContent>
                          {DEFAULT_INTERVAL_OPTIONS.map(opt => (
                            <SelectItem key={opt.days} value={String(opt.days)}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right: Channels + Message Template */}
              <div className="lg:col-span-5 space-y-6">
                {/* Channels */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Channels</CardTitle>
                    <CardDescription>Toggle delivery channels globally</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {CHANNEL_CONFIG.map(ch => (
                      <div key={ch.key} className="flex items-center justify-between py-2">
                        <div className="flex items-center gap-3">
                          <ch.icon className="h-5 w-5 text-muted-foreground" />
                          <span className="font-medium text-sm">{ch.label}</span>
                          {ch.mock && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                    Mock
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Delivery is simulated in this environment</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <Switch
                          data-testid={`payment-reminders-channel-${ch.key}-switch`}
                          checked={settings.channels?.[ch.key] || false}
                          onCheckedChange={() => toggleGlobalChannel(ch.key)}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Message Template */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Message Template</CardTitle>
                    <CardDescription>Available tokens: {'{client_name}'}, {'{invoice_no}'}, {'{amount}'}, {'{due_date}'}, {'{overdue_text}'}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      data-testid="payment-reminders-message-template"
                      value={settings.message_template || ''}
                      onChange={(e) => setSettings(s => ({ ...s, message_template: e.target.value }))}
                      rows={6}
                      className="font-mono text-sm"
                    />
                  </CardContent>
                </Card>

                {/* Save Button */}
                <Button data-testid="payment-reminders-save-settings-button" className="w-full" onClick={handleSaveSettings} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Settings
                </Button>
              </div>
            </div>
          ) : null}
        </TabsContent>

        {/* ===== CLIENT OVERRIDES TAB ===== */}
        <TabsContent value="clients" className="mt-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Per-Client Overrides</CardTitle>
                  <CardDescription>Customize reminder settings for individual clients</CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {clientsLoading ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : clientsData.items.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-muted-foreground">No clients found</p>
                </div>
              ) : (
                <Table data-testid="payment-reminders-client-overrides-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Override</TableHead>
                      <TableHead>Channels</TableHead>
                      <TableHead>Last Reminder</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientsData.items.map((client) => (
                      <TableRow key={client.id}>
                        <TableCell>
                          <p className="font-medium">{client.trade_name || client.full_name}</p>
                          <p className="text-xs text-muted-foreground">{client.contact_email || client.emirate}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant={client.override_enabled ? 'default' : 'secondary'}
                            className={client.override_enabled ? 'bg-primary/10 text-primary border-primary/20' : ''}>
                            {client.override_enabled ? 'Custom' : 'Global'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {client.override_enabled && client.override?.channels ? (
                            <div className="flex gap-1">
                              {Object.entries(client.override.channels).filter(([_, v]) => v).map(([k]) => {
                                const ch = CHANNEL_CONFIG.find(c => c.key === k);
                                return ch ? <ch.icon key={k} className="h-4 w-4 text-muted-foreground" /> : null;
                              })}
                            </div>
                          ) : <span className="text-xs text-muted-foreground">Using global</span>}
                        </TableCell>
                        <TableCell className="text-sm">
                          {client.last_reminder ? (
                            <span className="font-mono text-xs">{new Date(client.last_reminder.created_at).toLocaleDateString()}</span>
                          ) : <span className="text-muted-foreground">Never</span>}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditClient(client)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Client Override Dialog */}
          <Dialog open={!!editingClient} onOpenChange={() => setEditingClient(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Reminder Settings: {editingClient?.trade_name || editingClient?.full_name}</DialogTitle>
                <DialogDescription>Customize reminder behavior for this client.</DialogDescription>
              </DialogHeader>
              {clientOverride && (
                <div className="space-y-4 py-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">Enable Custom Override</Label>
                      <p className="text-xs text-muted-foreground">When disabled, global settings apply</p>
                    </div>
                    <Switch
                      checked={clientOverride.override_enabled}
                      onCheckedChange={(v) => setClientOverride(o => ({ ...o, override_enabled: v }))}
                    />
                  </div>

                  {clientOverride.override_enabled ? (
                    <>
                      <Separator />
                      <div>
                        <Label className="text-sm font-medium mb-2 block">Channels</Label>
                        <div className="space-y-2">
                          {CHANNEL_CONFIG.map(ch => (
                            <div key={ch.key} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <ch.icon className="h-4 w-4" />
                                <span className="text-sm">{ch.label}</span>
                                {ch.mock && <Badge variant="outline" className="text-[10px] px-1">Mock</Badge>}
                              </div>
                              <Switch
                                checked={clientOverride.channels?.[ch.key] || false}
                                onCheckedChange={(v) => setClientOverride(o => ({ ...o, channels: { ...o.channels, [ch.key]: v } }))}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm">Notes</Label>
                        <Input
                          value={clientOverride.notes || ''}
                          onChange={(e) => setClientOverride(o => ({ ...o, notes: e.target.value }))}
                          placeholder="Optional notes about this client's preferences"
                        />
                      </div>
                    </>
                  ) : (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>Global reminder settings will be applied to this client.</AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingClient(null)}>Cancel</Button>
                <Button onClick={handleSaveClientOverride} disabled={clientSaving}>
                  {clientSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save Override
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ===== REMINDER LOG TAB ===== */}
        <TabsContent value="log" className="mt-6 space-y-4">
          {/* Log Stats */}
          {logStats && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { label: 'Total Sent', value: logStats.total_sent, icon: Send, color: 'text-primary' },
                { label: 'Sent Today', value: logStats.sent_today, icon: Clock, color: 'text-blue-600' },
                { label: 'Last 30 Days', value: logStats.sent_last_30_days, icon: RefreshCw, color: 'text-emerald-600' },
                { label: 'Failed', value: logStats.total_failed, icon: XCircle, color: logStats.total_failed > 0 ? 'text-red-500' : 'text-emerald-600' },
                { label: 'By Email', value: logStats.by_channel?.email || 0, icon: Mail, color: 'text-amber-600' }
              ].map(kpi => (
                <Card key={kpi.label}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
                        <p className="text-xl font-bold font-mono tabular-nums mt-0.5">{kpi.value}</p>
                      </div>
                      <kpi.icon className={cn('h-6 w-6 opacity-50', kpi.color)} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Log Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Select value={logFilters.channel} onValueChange={(v) => setLogFilters(f => ({ ...f, channel: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Channel" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Channels</SelectItem>
                    {CHANNEL_CONFIG.map(ch => <SelectItem key={ch.key} value={ch.key}>{ch.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={logFilters.status} onValueChange={(v) => setLogFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Log Table */}
          <Card>
            <CardContent className="p-0">
              {logsLoading ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : logs.items.length === 0 ? (
                <div className="p-12 text-center">
                  <Bell className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
                  <h3 className="font-semibold">No reminders sent yet</h3>
                  <p className="text-sm text-muted-foreground mt-1">Reminders will appear here once the scheduler runs.</p>
                </div>
              ) : (
                <Table data-testid="payment-reminders-log-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.items.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-xs">{new Date(log.created_at).toLocaleString()}</TableCell>
                        <TableCell className="font-medium text-sm">{log.client_name}</TableCell>
                        <TableCell className="font-mono text-sm">{log.invoice_no}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{log.trigger_label}</Badge></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {(() => { const ch = CHANNEL_CONFIG.find(c => c.key === log.channel); return ch ? <ch.icon className="h-3.5 w-3.5" /> : null; })()}
                            <span className="text-sm capitalize">{log.channel?.replace('_', ' ')}</span>
                            {log.mock && <Badge variant="outline" className="text-[9px] px-1">Mock</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={log.status === 'failed' ? 'destructive' : 'default'}
                            className={log.status !== 'failed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}>
                            {log.status === 'sent' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                            {log.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
